import type { Route } from "../shell/routes";
import { installNativeNip05 } from "./nativeNip05";
import { isDesktopShell } from "./runtime";
import { useDesktopNotifications } from "./useDesktopNotifications";
import { useKeychainRestore } from "./useKeychain";
import { useNostrDeepLinks } from "./useNostrDeepLinks";

/*
 * At module scope, and that is the point.
 *
 * A verification outcome is cached for minutes, so a transport installed from an
 * effect would arrive after the first feed had already checked — and cached as
 * failed — exactly the handles the native path exists to rescue. Module evaluation
 * runs when `App.tsx` is imported, before anything renders.
 *
 * A no-op in a browser: `installNativeNip05` checks for the shell and leaves the
 * default `fetch` transport in place.
 */
installNativeNip05();

export interface DesktopBridgeProps {
  onNavigate(route: Route): void;
  onOpenThread(id: string): void;
}

/**
 * The desktop shell's native features, in one mount.
 *
 * One component rather than three hook calls in `App.tsx`, because the shell is
 * shared with the browser build and every line added there is a line the web app
 * carries. This is the whole footprint: one element, two callbacks.
 */
export function DesktopBridge(props: DesktopBridgeProps) {
  /*
   * Whether we are in the shell is a constant for the life of the document — a page
   * cannot move between a webview and a browser tab — so this branch cannot reorder
   * hooks between renders, and the features below can be plain hooks instead of
   * hooks that check a flag internally.
   *
   * It matters that they are not mounted in the browser at all: the notification
   * watcher resolves author profiles, which is real relay traffic in aid of a
   * capability a browser does not have.
   */
  if (!isDesktopShell()) return null;
  return <DesktopFeatures {...props} />;
}

function DesktopFeatures({ onNavigate, onOpenThread }: DesktopBridgeProps) {
  useKeychainRestore();
  useNostrDeepLinks({ onNavigate, onOpenThread });
  useDesktopNotifications();
  // Nothing to draw. Every feature here is either an OS surface or a state change
  // in a provider that is already mounted above.
  return null;
}
